import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SpinningIco } from "@/components/brand/SpinningIco";
import { Wordmark } from "@/components/brand/Wordmark";
import { supabase } from "@/lib/supabase";

/**
 * /auth/reset-password - landing page for recovery emails.
 *
 * Flow:
 *   1. User requested reset on /forgot-password → Supabase sent email
 *      with link containing ?code=xxx redirecting back here.
 *   2. The Supabase browser client has `detectSessionInUrl: true`, so
 *      on page mount it automatically exchanges the code (using the
 *      code_verifier stashed in localStorage by step 1) for a recovery
 *      session. We just wait for that to complete.
 *   3. Once a session is present and an `error_description` isn't in the
 *      URL, show the new-password form.
 *   4. On submit, call updateUser({ password }) and bounce to login.
 *
 * Edge cases:
 *   - Link opened in a different browser than the one that requested it:
 *     code_verifier missing → exchange fails → we surface that as
 *     "link expired" and offer to request a fresh one.
 *   - Link clicked twice: same outcome - Supabase invalidates after the
 *     first exchange.
 */
export function AuthResetPasswordPage() {
  const navigate = useNavigate();

  type State =
    | { kind: "verifying" }
    | { kind: "ready" }
    | { kind: "error"; message: string }
    | { kind: "success" };

  const [state, setState] = useState<State>({ kind: "verifying" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const validPassword = password.length >= 8;
  const passwordsMatch = password === confirm;

  useEffect(() => {
    // Surface a Supabase-emitted error from the URL (e.g.
    // ?error_description=Token+has+expired) before doing anything else.
    const params = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const errDescription =
      params.get("error_description") ?? hash.get("error_description");
    if (errDescription) {
      setState({ kind: "error", message: errDescription });
      return;
    }

    // detectSessionInUrl runs on every load - give it a beat to swap the
    // ?code= for a session, then check what we ended up with. We also
    // subscribe to onAuthStateChange so if the session lands a moment
    // later we pick it up rather than racing it.
    let settled = false;

    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (settled) return;
      // PASSWORD_RECOVERY fires when Supabase has consumed the recovery
      // code and minted a temporary recovery session. That's our signal
      // to let the user choose a new password.
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        settled = true;
        setState({ kind: "ready" });
      }
    });

    // Fallback: if nothing fires within 4 seconds we probably never had
    // a valid code to begin with (link opened in a different browser,
    // already used, etc.).
    const timeout = window.setTimeout(() => {
      if (settled) return;
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (settled) return;
        settled = true;
        if (session) {
          setState({ kind: "ready" });
        } else {
          setState({
            kind: "error",
            message:
              "This reset link is expired or was opened in a different browser. Request a fresh one below.",
          });
        }
      });
    }, 4000);

    return () => {
      sub.data.subscription.unsubscribe();
      window.clearTimeout(timeout);
    };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validPassword || !passwordsMatch) return;

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (error) {
      setState({ kind: "error", message: error.message });
      return;
    }
    setState({ kind: "success" });
    // After 2 seconds, sign them out of the recovery session and route
    // to login - they should sign back in fresh with the new password.
    window.setTimeout(async () => {
      await supabase.auth.signOut();
      navigate("/login", { replace: true });
    }, 2000);
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-12">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[640px] opacity-30 blur-3xl"
        style={{
          backgroundImage:
            "radial-gradient(closest-side at 50% 0%, hsl(var(--volt)), transparent 70%)",
        }}
      />

      <div className="w-full max-w-md">
        <div className="mb-10 flex flex-col items-center gap-3">
          <SpinningIco size={120} />
          <Wordmark size="text-3xl" />
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl sm:p-8">
          {state.kind === "verifying" && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-volt" />
              <p className="text-sm text-muted-foreground">
                Verifying your reset link…
              </p>
            </div>
          )}

          {state.kind === "ready" && (
            <>
              <div className="mb-5">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-volt/30 bg-volt-subtle px-3 py-1 text-xs font-medium">
                  <KeyRound className="h-3.5 w-3.5 text-volt" aria-hidden />
                  Recovery verified
                </div>
                <h1 className="text-2xl font-semibold tracking-tight">
                  Choose a new password
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  At least 8 characters. We'll send you to sign in once it's
                  saved.
                </p>
              </div>

              <form onSubmit={onSubmit} className="flex flex-col gap-4">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium">New password</span>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium">Confirm new password</span>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    placeholder="Type it again"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                  {confirm.length > 0 && !passwordsMatch && (
                    <span className="text-xs text-destructive">
                      Passwords don't match.
                    </span>
                  )}
                </label>

                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting || !validPassword || !passwordsMatch}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    "Save password"
                  )}
                </Button>
              </form>
            </>
          )}

          {state.kind === "error" && (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-destructive/40 bg-destructive/10 text-destructive">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">Link expired</h2>
                <p className="text-sm text-muted-foreground">{state.message}</p>
              </div>
              <Button asChild size="lg">
                <Link to="/forgot-password">Request a new link</Link>
              </Button>
            </div>
          )}

          {state.kind === "success" && (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-volt text-primary-foreground">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">Password saved</h2>
                <p className="text-sm text-muted-foreground">
                  Redirecting you to sign in…
                </p>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Trouble with the link? Email{" "}
          <a
            href="mailto:hello@whistll.com?subject=Password%20reset"
            className="underline underline-offset-2 hover:text-foreground"
          >
            hello@whistll.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}
